import React, { useEffect, useState } from 'react';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';
import { AvailabilityToggle } from '@components/atoms/AvailabilityToggle';
import { useToggleShareLocation } from '@services/useToggleShareLocation';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';
import styles from './AvailabilityWithPicker.module.css';

export const AvailabilityWithPicker: React.FC = () => {
    const { t } = useTranslation();
    const { request: requestShareLocation, error, lastResponseTime } = useToggleShareLocation();
    const { showDefaultNotification } = useNotifications();
    const { user, lastLocationSaved, setLastLocationSaved } = useData();
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [geoError, setGeoError] = useState<string | null>(null);
    const [available, setAvailable] = useState<boolean | null>(null);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [geoPermission, setGeoPermission] = useState<PermissionState | null>(null);

    useEffect(() => {
        if (user) {
            setAvailable(!!user?.data?.share_location);
        }
    }, [user]);

    useEffect(() => {
        if (lastResponseTime) {
            if (error) {
                showDefaultNotification({ type: 'error', message: error });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    useEffect(() => {
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
            setGeoPermission(status.state);
            status.onchange = () => setGeoPermission(status.state);
        });
    }, []);

    const handleToggle = (value: boolean) => {
        if (geoPermission != 'granted') {
            setPickerVisible(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        coordinates: [position.coords.latitude, position.coords.longitude] as [number, number],
                        lastUpdated: new Date().toISOString(),
                        locationAccessGranted: true,
                    };
                    setLastLocationSaved(location);
                    setGeoPermission('granted');
                },
                () => setGeoPermission('denied')
            );
        } else {
            setPickerVisible(false);

            if (value == true) {
                handleButtonClick(true);
            } else {
                setAvailable(false);
                requestShareLocation({
                    share_location: false,
                });
            }
        }
    };

    const handleButtonClick = async (confirm: boolean) => {
        setPickerVisible(false);

        if (!confirm) {
            setAvailable(false);
            return;
        }

        if (geoError) {
            showDefaultNotification({ type: 'error', message: geoError });
            setAvailable(false);
            return;
        }

        const coords = lastLocationSaved?.coordinates ?? [latitude, longitude];

        if (!coords[0] || !coords[1]) {
            showDefaultNotification({ type: 'error', message: t('availability-toggle.missing-coordenates') });
            return;
        }

        try {
            const hasPermission = await checkGeolocationPermission();
            if (!hasPermission) {
                showDefaultNotification({ type: 'error', message: t('availability-toggle.missing-geo-permission') });
                return;
            }

            await requestShareLocation({
                share_location: true,
                latitude: coords[0],
                longitude: coords[1],
            });
            setAvailable(true);
        } catch (err) {
            showDefaultNotification({ type: 'error', message: t('availability-toggle.error-sharing-location') });
        }
    };

    const checkGeolocationPermission = async (): Promise<boolean> => {
        try {
            const status = await navigator.permissions.query({ name: 'geolocation' });
            if (status.state === 'granted') return true;
            if (status.state === 'prompt') return true;
            return false;
        } catch (err) {
            return false;
        }
    };

    console.log('----', checkGeolocationPermission());

    return (
        <div className={styles.wrapper}>
            {pickerVisible && (
                <div className={styles.overlay}>
                    <div className={styles.pickerContainer}>
                        <RoundProfilePicture
                            icon="fa-thin fa-location-dot"
                            labelText={t('availability-toggle.location')}
                            hintText={t('availability-toggle.set-location')}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        <Button
                            label={t('availability-toggle.auto-location')}
                            mode={ButtonMode.primary}
                            classes={styles.button}
                            onClick={() => handleButtonClick(true)}
                        />
                        <Button
                            label={t('availability-toggle.later')}
                            mode={ButtonMode.primary}
                            classes={styles.button}
                            outline
                            onClick={() => handleButtonClick(false)}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    </div>
                </div>
            )}

            <AvailabilityToggle available={available ?? false} onToggle={handleToggle} />
        </div>
    );
};
